import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Flame, TrendingUp, Mic, Sparkles, Volume2, Award } from 'lucide-react';
import axios from 'axios';
import { getDeviceId } from '../lib/deviceId';
import { tone } from '../lib/score';
import { soundInfo, focusForSound } from '../lib/phonetics';
import { progressCoach, recommend } from '../lib/coach';
import { useSpeaker } from '../lib/speak';
import TrendChart from '../components/brand/TrendChart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METRIC_LABELS = [
  ['accuracy', 'Accuracy', 'Each sound said the way it should be'],
  ['fluency', 'Fluency', 'Smooth flow, pauses in the right places'],
  ['prosody', 'Melody', 'Stress and pitch that carry meaning'],
  ['completeness', 'Completeness', 'Every word said'],
];

const Stat = ({ label, value, unit, children }) => (
  <div className="cz-card p-5">
    <p className="text-xs text-white/45">{label}</p>
    <p className="mt-1 flex items-baseline gap-1">
      <span className="text-3xl font-semibold tabular-nums text-white">{value}</span>
      {unit && <span className="text-sm text-white/40">{unit}</span>}
    </p>
    {children}
  </div>
);

const Dashboard = () => {
  const [progress, setProgress] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const speaker = useSpeaker();

  useEffect(() => {
    axios
      .get(`${API}/progress?user_id=${getDeviceId()}`)
      .then((res) => setProgress(res.data))
      .catch((error) => console.error('Error fetching progress:', error))
      .finally(() => setLoading(false));
    axios.get(`${API}/exercises`).then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const coach = useMemo(() => (progress ? progressCoach(progress) : null), [progress]);
  const picks = useMemo(() => {
    if (!coach || !catalog.length) return [];
    const done = new Set((progress.insights?.history || []).map((h) => h.exercise_id));
    return coach.focuses
      .map((f) => ({ focus: f, ex: recommend(catalog.filter((e) => !done.has(e.id)), f, { n: 1 })[0] }))
      .filter((p) => p.ex);
  }, [coach, catalog, progress]);

  if (loading) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <Loader2 className="h-10 w-10 animate-spin text-brand-400" />
      </div>
    );
  }

  const sessions = progress?.insights?.sessions || progress?.completed_exercises || 0;

  if (!progress || sessions === 0) {
    return (
      <div className="mx-auto grid min-h-[60vh] max-w-md place-items-center px-5 text-center">
        <div className="animate-fade-up">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-brand-500/15 text-brand-300">
            <TrendingUp className="h-7 w-7" />
          </div>
          <h1 className="mt-5 text-2xl font-semibold text-white">Your progress starts here</h1>
          <p className="mt-2 text-sm leading-relaxed text-white/55">
            Finish your first practice and Cadenza starts tracking your scores, your strong sounds and the ones to work on.
          </p>
          <Link to="/practice" className="cz-btn-primary mt-7">
            <Mic className="h-4 w-4" /> Record your first practice
          </Link>
        </div>
      </div>
    );
  }

  const ins = progress.insights || {};
  const avg = Math.round(progress.average_score || 0);
  const delta = ins.trend?.delta;

  return (
    <div className="mx-auto max-w-6xl px-5 pt-8 sm:pt-12">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between animate-fade-up">
        <div>
          <p className="cz-eyebrow">Progress</p>
          <h1 className="mt-3 text-4xl font-semibold tracking-[-0.03em] text-white sm:text-5xl">
            Keep your <span className="cz-display text-brand-400">streak alive.</span>
          </h1>
        </div>
        <Link to="/practice" className="cz-btn-primary self-start sm:self-auto">
          Continue practicing <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Coach's note */}
      <section className="cz-card mt-8 border-brand-400/20 bg-brand-500/[0.06] p-6 sm:p-8">
        <h2 className="flex items-center gap-2 font-semibold text-white"><Sparkles className="h-4 w-4 text-brand-300" /> Coach’s note</h2>
        <div className="mt-3 space-y-2 text-[15px] leading-relaxed text-white/75">
          {coach.lines.map((l) => <p key={l}>{l}</p>)}
        </div>
      </section>

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Average score" value={avg} unit="/100">
          <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/[0.06]">
            <div className={`h-full rounded-full bg-gradient-to-r ${tone(avg).bar}`} style={{ width: `${avg}%` }} />
          </div>
        </Stat>
        <Stat label="Trend" value={delta == null ? '—' : `${delta > 0 ? '+' : ''}${Math.round(delta)}`} unit={delta == null ? '' : 'pts'}>
          <p className="mt-2 text-xs text-white/40">{delta == null ? 'After four sessions' : `Last ${ins.trend.window} vs before`}</p>
        </Stat>
        <Stat label="Streak" value={progress.weekly_streak || 0} unit="days this week">
          <p className="mt-2 flex items-center gap-1 text-xs text-coral-300"><Flame className="h-3.5 w-3.5" /> Practice today to keep it</p>
        </Stat>
        <Stat label="Sessions" value={sessions}>
          <p className="mt-2 text-xs text-white/40">{progress.completed_exercises} of {progress.total_exercises} exercises tried</p>
        </Stat>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="cz-card min-w-0 p-6">
          <h2 className="font-semibold text-white">Score per session</h2>
          <p className="mt-1 text-xs text-white/40">Your last {ins.history?.length || 0} sessions</p>
          <div className="mt-4"><TrendChart history={ins.history} /></div>
        </div>
        <div className="cz-card p-6">
          <h2 className="font-semibold text-white">Your skills</h2>
          <p className="mt-1 text-xs text-white/40">Averages across all sessions</p>
          <div className="mt-5 space-y-4">
            {METRIC_LABELS.filter(([k]) => ins.metrics?.[k] != null).map(([k, label, sub]) => {
              const v = ins.metrics[k];
              const t = tone(v);
              return (
                <div key={k}>
                  <div className="mb-1.5 flex items-baseline justify-between gap-3">
                    <span className="text-sm text-white/85">{label} <span className="ml-1 hidden text-xs text-white/35 sm:inline">{sub}</span></span>
                    <span className={`font-semibold tabular-nums ${t.text}`}>{Math.round(v)}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                    <div className={`h-full rounded-full bg-gradient-to-r ${t.bar}`} style={{ width: `${v}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {ins.weak_sounds?.length > 0 && (
        <section className="mt-10">
          <h2 className="text-lg font-semibold text-white">Sounds to work on</h2>
          <p className="mt-1 text-sm text-white/45">Your lowest-scoring sounds over your recent sessions.</p>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            {ins.weak_sounds.map((s) => {
              const inf = soundInfo(s.phoneme);
              if (!inf) return null;
              const t = tone(s.avg);
              return (
                <div key={s.phoneme} className={`rounded-2xl border p-5 ${t.soft}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-2xl font-semibold text-white">
                        “{inf.say}” <span className="font-mono text-lg text-white/55">/{inf.ipa}/</span>
                      </p>
                      <p className="text-sm text-white/55">as in “{inf.ex}” · {s.count} tries</p>
                    </div>
                    <span className={`text-2xl font-semibold tabular-nums ${t.text}`}>{Math.round(s.avg)}</span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-white/75"><b className="text-white">Usually {inf.slip}.</b> {inf.tip}</p>
                  {s.examples?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {s.examples.map((w) => (
                        <button
                          key={w}
                          type="button"
                          onClick={() => speaker.speak(w)}
                          disabled={!speaker.canSpeak}
                          className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-sm text-white transition-colors hover:bg-white/20 disabled:cursor-default"
                        >
                          {speaker.canSpeak && <Volume2 className="h-3.5 w-3.5 text-white/60" />}{w}
                        </button>
                      ))}
                    </div>
                  )}
                  <Link to={`/practice?focus=${encodeURIComponent(focusForSound(s.phoneme))}`} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-white hover:text-brand-200">
                    Practice {focusForSound(s.phoneme)} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {ins.trouble_words?.length > 0 && (
          <div className="cz-card p-6">
            <h2 className="font-semibold text-white">Words you keep tripping on</h2>
            <p className="mt-1 text-xs text-white/40">Tap to hear how they should sound.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {ins.trouble_words.map((w) => (
                <button
                  key={w.word}
                  type="button"
                  onClick={() => speaker.speak(w.word)}
                  disabled={!speaker.canSpeak}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-400/20 bg-rose-400/10 px-3 py-1.5 text-sm text-rose-100 transition-colors hover:bg-rose-400/20 disabled:cursor-default"
                >
                  {w.word}
                  <span className="text-[11px] text-rose-200/60">{w.times}×</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {ins.strong_sounds?.length > 0 && (
          <div className="cz-card p-6">
            <h2 className="flex items-center gap-2 font-semibold text-white"><Award className="h-4 w-4 text-emerald-300" /> Your strongest sounds</h2>
            <p className="mt-1 text-xs text-white/40">These are solid — no need to drill them.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {ins.strong_sounds.map((s) => {
                const inf = soundInfo(s.phoneme);
                return inf ? (
                  <span key={s.phoneme} className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1.5 text-sm text-emerald-100">
                    “{inf.say}” /{inf.ipa}/ <span className="ml-1 text-[11px] text-emerald-200/60">{Math.round(s.avg)}</span>
                  </span>
                ) : null;
              })}
            </div>
          </div>
        )}
      </div>

      {picks.length > 0 && (
        <section className="mt-10">
          <p className="cz-eyebrow">Recommended for you</p>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            {picks.map(({ focus, ex }) => (
              <Link key={ex.id} to={`/practice?ex=${ex.id}`} className="cz-card group flex flex-col p-5 transition-colors hover:border-white/15">
                <span className="text-xs font-medium text-brand-300">{focus}</span>
                <p className="mt-2 font-semibold text-white">{ex.title}</p>
                <p className="mt-2 line-clamp-3 flex-1 text-sm text-white/50">{ex.target_phrase}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-white">
                  Start <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="cz-card mt-10 p-6">
        <h2 className="font-semibold text-white">Recent sessions</h2>
        <ul className="mt-4 divide-y divide-white/[0.06]">
          {progress.recent_activity?.length > 0 ? progress.recent_activity.map((a, i) => {
            const t = tone(a.score);
            return (
              <li key={i} className="flex items-center gap-4 py-3">
                <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl border text-sm font-semibold tabular-nums ${t.soft}`}>{Math.round(a.score)}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-white">{a.exercise}</p>
                  <p className="text-xs text-white/40">{a.date}</p>
                </div>
              </li>
            );
          }) : <li className="py-3 text-sm text-white/45">No sessions yet.</li>}
        </ul>
      </div>
    </div>
  );
};

export default Dashboard;
